<template>
  <app-page
    title="Create an open award"
    subtitle="Set the basics, choose who can earn it, and personalise the award card."
    :previous="[{ label: 'Recognition', to: { name: 'recognition' } }]"
  >
    <template #custom-content>
      <b-col>
        <ValidationObserver v-slot="v">
          <b-form id="create-award-form" @submit.prevent="v.handleSubmit(onSubmit)">
            <div class="form-container">
              <!-- Basic Details -->
              <basic-details-section
                :name="awardForm.name"
                :selected-division-ids="selectedDivisionIds"
                :division-options="divisionOptions"
                @update:name="awardForm.name = $event"
                @update:selectedDivisionIds="onDivisionsChange"
              >
                <template #after-divisions>
                  <hr />

                  <div class="company-values-container">
                    <div class="label-row">
                      <div class="label">
                        Add your company values (optional)
                        <small>
                          Tag relevant values to help employees understand what behaviours this award recognises. You
                          can use our suggestions or customise them to match your organisation's values.
                        </small>
                      </div>
                      <b-form-checkbox v-model="companyValuesEnabled" switch />
                    </div>
                    <div v-if="companyValuesEnabled">
                      <create-company-values-modal v-model="companyValues" />
                    </div>
                  </div>

                  <hr />

                  <div class="short-description-container">
                    <div class="label">
                      Short description (optional)
                      <small>
                        A short description with extra detail about this award — for example its location or budget
                        source.
                      </small>
                    </div>
                    <app-input
                      v-model="shortDescription"
                      type="text"
                      maxlength="500"
                      placeholder="Add a short description (optional)"
                    />
                  </div>
                </template>
              </basic-details-section>

              <!-- Award Details -->
              <div>
                <h4>Award details</h4>
                <sme-card large>
                  <monetary-reward-section
                    :enabled="monetaryRewardEnabled"
                    :currency-symbol="currencySymbol"
                    :amounts="awardValueMinor"
                    :default-amount-minor="defaultAwardValueMinor"
                    :amount-error="awardValueError"
                    :redemption-options="selectedRedemptionOptions"
                    :redemption-error="redemptionSelectionError"
                    :expiration-months="expirationMonths"
                    @update:enabled="monetaryRewardEnabled = $event"
                    @update:amounts="awardValueMinor = $event"
                    @update:defaultAmountMinor="onDefaultAwardValueMinorChange"
                    @update:redemptionOptions="selectedRedemptionOptions = $event"
                    @update:expirationMonths="expirationMonths = $event"
                  >
                    <template #reward-amounts>
                      <div class="amount-mode-container">
                        <b-form-radio v-model="amountMode" name="amount-mode" value="fixed">
                          Set fixed reward amount(s)
                        </b-form-radio>
                        <b-form-radio v-model="amountMode" name="amount-mode" value="free">
                          Set adjustable reward amount
                        </b-form-radio>
                      </div>

                      <div v-if="amountMode === 'fixed'" class="reward-amount-container">
                        <div class="label">
                          Reward amount options
                          <small>
                            Choose one or more values that can be awarded with this prize. If you select multiple, the
                            person suggesting a reward can choose the amount.
                          </small>
                        </div>
                        <create-reward-amounts-modal
                          :value="awardValueMinor"
                          :default-minor="defaultAwardValueMinor"
                          :currency-symbol="currencySymbol"
                          @input="awardValueMinor = $event"
                          @update:defaultMinor="onDefaultAwardValueMinorChange"
                        />
                        <small v-if="awardValueError" class="text-danger">{{ awardValueError }}</small>
                      </div>

                      <div v-else class="reward-amount-container">
                        <app-input
                          :value="maxAwardValue"
                          type="text"
                          inputmode="numeric"
                          :prepend="currencySymbol"
                          placeholder="e.g. 50"
                          @input="onMaxAwardValueInput"
                        >
                          <template #label>
                            <div class="label">
                              Maximum amount
                              <small>The most a nominator can award.</small>
                            </div>
                          </template>
                        </app-input>
                        <small v-if="maxAwardValueError" class="text-danger">{{ maxAwardValueError }}</small>
                      </div>
                    </template>

                    <template #after-amounts>
                      <budget-settings-section
                        v-if="monetaryRewardEnabled"
                        :currency-symbol="currencySymbol"
                        :amount="rewardBudgetAmount"
                        :cadence="rewardBudgetCadence"
                        :year-start-month="budgetYearStartMonth"
                        :year-start-day="budgetYearStartDay"
                        :year-start-day-options="budgetYearStartDayOptions"
                        :year-start-disabled="budgetYearStartDisabled"
                        :year-start-month-rules="budgetYearStartMonthRules"
                        :year-start-day-rules="budgetYearStartDayRules"
                        @update:amount="rewardBudgetAmount = $event"
                        @update:cadence="rewardBudgetCadence = $event"
                        @update:yearStartMonth="budgetYearStartMonth = $event"
                        @update:yearStartDay="budgetYearStartDay = $event"
                      />

                      <div v-if="monetaryRewardEnabled" class="categories-container">
                        <div class="label-row">
                          <div class="label">
                            Add spend categories (optional)
                            <small>Let people tag what a reward is for, so you can track spending by category.</small>
                          </div>
                          <b-form-checkbox v-model="categoriesEnabled" switch />
                        </div>
                        <div v-if="categoriesEnabled">
                          <create-categories-modal v-model="categories" />
                          <b-form-checkbox v-model="categoryRequired" class="mt-2" :disabled="!categories.length">
                            Make selecting a category mandatory
                          </b-form-checkbox>
                          <b-form-checkbox v-model="showCategoryEnabled" class="mt-2" :disabled="!categories.length">
                            Show the category to employees in their feed
                          </b-form-checkbox>
                        </div>
                      </div>
                    </template>
                  </monetary-reward-section>
                </sme-card>
              </div>

              <!-- Nominations & Approvals -->
              <div v-if="monetaryRewardEnabled">
                <h4>Nominations & Approvals</h4>
                <sme-card large>
                  <div class="label-permissions-and-approvals">
                    Permissions and approvals
                    <small>
                      This award is <span class="highlighted-text">open</span>: anyone can send recognition.
                      Recognitions with monetary rewards will require approval from a reviewer.
                    </small>
                  </div>

                  <div class="approver-container">
                    <multi-select
                      id="approvers-select"
                      :value="selectedApproverIds"
                      :options="allEmployeeOptions"
                      placeholder="Select who can approve this award"
                      :searchable="true"
                      search-placeholder="Search by name or email"
                      :disabled="employeesLoading"
                      :select-all="false"
                      select-all-text="No approvers available"
                      class="mb-0"
                      @input="onApproversChange"
                    >
                      <template #label>
                        <div class="label">Who can approve?</div>
                      </template>
                    </multi-select>
                    <small v-if="approverSelectionError" class="text-danger">{{ approverSelectionError }}</small>
                  </div>
                </sme-card>
              </div>
            </div>
          </b-form>
        </ValidationObserver>
      </b-col>
    </template>

    <template #footer>
      <div class="d-flex align-items-center justify-content-end w-100 px-4 py-3">
        <small v-if="errorMessage" class="text-danger mr-3">{{ errorMessage }}</small>
        <b-button variant="outline-secondary" class="mr-2" :disabled="saving" @click="$router.back()">Cancel</b-button>
        <b-button type="submit" variant="primary" :disabled="saving" form="create-award-form">
          <b-spinner v-if="saving" class="mr-2" small />
          {{ saving ? 'Creating...' : 'Create award' }}
        </b-button>
      </div>
    </template>
  </app-page>
</template>

<script setup>
import { ValidationObserver } from 'vee-validate';
import { onBeforeMount, ref, watch } from 'vue';
import { useRouter } from 'vue-router/composables';
import ApiClient from '@/ApiClient';
import { RECOGNITION_AWARD_TYPES, RECOGNITION_APPROVAL_TYPES } from '@/pages/recognition/constants';
import { useAwardForm } from '@/pages/recognition/composables/useAwardForm';
import { useDivisionEmployeeFiltering } from '@/pages/recognition/composables/useDivisionEmployeeFiltering';
import AppInput from '@/components/AppInput.vue';
import AppPage from '@/components/AppPage.vue';
import MultiSelect from '@/components/MultiSelect.vue';
import CreateCompanyValuesModal from '@/pages/recognition/components/CreateCompanyValuesModal.vue';
import CreateCategoriesModal from '@/pages/recognition/components/CreateCategoriesModal.vue';
import CreateRewardAmountsModal from '@/pages/recognition/components/CreateRewardAmountsModal.vue';
import useToast from '@/composables/useToast';
import SmeCard from '@/components/atoms/SmeCard.vue';
import BasicDetailsSection from '@/pages/recognition/components/BasicDetailsSection.vue';
import MonetaryRewardSection from '@/pages/recognition/components/MonetaryRewardSection.vue';
import BudgetSettingsSection from '@/pages/recognition/components/BudgetSettingsSection.vue';

// Use shared composable
const {
  currencySymbol,
  awardForm,
  selectedDivisionIds,
  divisionOptions,
  enabledDivisions,
  monetaryRewardEnabled,
  amountMode,
  awardValueMinor,
  awardValueError,
  maxAwardValue,
  maxAwardValueError,
  defaultAwardValueMinor,
  selectedRedemptionOptions,
  redemptionSelectionError,
  expirationMonths,
  rewardBudgetAmount,
  rewardBudgetCadence,
  budgetYearStartMonth,
  budgetYearStartDay,
  budgetYearStartDayOptions,
  budgetYearStartDisabled,
  budgetYearStartMonthRules,
  budgetYearStartDayRules,
  categoriesEnabled,
  categories,
  categoryRequired,
  showCategoryEnabled,
  shortDescription,
  saving,
  errorMessage,
  onMaxAwardValueInput,
  onDefaultAwardValueMinorChange,
  loadDivisions,
  initBudgetYearStart,
  setupCommonWatchers,
  resetMonetaryRewardFields,
  buildBaseProperties,
  getBudgetMinor,
  getGrantExpiryInterval,
  persistBudgetYearStart,
  validateRedemptionOptions,
  validateAwardValues,
  validateBudgetCadence,
} = useAwardForm();

// Division-based employee filtering
const { employeesLoading, allEmployeeOptions, loadEmployeesAndDivisionPairs } =
  useDivisionEmployeeFiltering(selectedDivisionIds);

// Open-specific state
const companyValuesEnabled = ref(false);
const companyValues = ref([]);
const selectedApproverIds = ref([]);
const approverSelectionError = ref('');

onBeforeMount(async () => {
  await Promise.all([loadDivisions(), ...loadEmployeesAndDivisionPairs()]);
  initBudgetYearStart();
});

const $router = useRouter();
const { doToast, TOAST_VARIANT } = useToast();

// Setup common watchers
setupCommonWatchers();

const buildProperties = (approverIds, redemptionMethods) => {
  const props = buildBaseProperties(redemptionMethods);

  props.approvers = (approverIds || []).length
    ? [
        {
          name: 'Default',
          approvers: (approverIds || []).map(String),
        },
      ]
    : undefined;

  if (companyValuesEnabled.value) {
    const values = (companyValues.value || []).map(v => String(v || '').trim()).filter(Boolean);
    if (values.length) {
      props.company_values = values.map(name => ({ name }));
    }
  }

  return props;
};

const buildPayload = (properties, approvers) => ({
  program_type: RECOGNITION_AWARD_TYPES.OPEN,
  program_name: awardForm.value.name,
  approval_type: (approvers || []).length > 0 ? RECOGNITION_APPROVAL_TYPES.AMOUNT : RECOGNITION_APPROVAL_TYPES.NONE,
  show_badge: false,
  enabled_divisions: enabledDivisions.value,
  budget_minor: getBudgetMinor(),
  grant_expiry_interval: getGrantExpiryInterval(),
  properties,
});

const onSubmit = async () => {
  errorMessage.value = '';
  saving.value = true;
  const approvers = monetaryRewardEnabled.value ? selectedApproverIds.value || [] : [];
  const redemptionMethods = monetaryRewardEnabled.value ? selectedRedemptionOptions.value || [] : [];

  try {
    if (!validateRedemptionOptions(redemptionMethods)) {
      saving.value = false;
      return;
    }

    // Require at least one approver when monetary rewards are enabled
    if (monetaryRewardEnabled.value && (!approvers || approvers.length === 0)) {
      approverSelectionError.value = 'Please select at least one approver.';
      saving.value = false;
      return;
    }
    approverSelectionError.value = '';

    if (!validateAwardValues()) {
      saving.value = false;
      return;
    }

    if (!validateBudgetCadence()) {
      saving.value = false;
      return;
    }

    const approverIds = (approvers || []).map(String);
    const properties = buildProperties(approverIds, redemptionMethods);
    const payload = buildPayload(properties, approvers);

    const budgetResult = await persistBudgetYearStart();
    if (!budgetResult.success) {
      errorMessage.value = budgetResult.error;
      saving.value = false;
      return;
    }

    await ApiClient.createRecognitionProgram(payload);

    doToast('Success', 'Open award created', TOAST_VARIANT.SUCCESS);
    $router.push({ name: 'recognition' });
  } catch (e) {
    errorMessage.value = e?.variables?.message || e?.message || 'Failed to create award';
  } finally {
    saving.value = false;
  }
};

const onDivisionsChange = ids => {
  // Approvers can be any company employee (HERO-2974) and open awards have no
  // division-scoped nominator/nominee picker here, so nothing is pruned on change.
  selectedDivisionIds.value = ids;
};

const onApproversChange = ids => {
  selectedApproverIds.value = ids;
};

watch(selectedApproverIds, newVal => {
  if (approverSelectionError.value && Array.isArray(newVal) && newVal.length > 0) {
    approverSelectionError.value = '';
  }
});

watch(monetaryRewardEnabled, enabled => {
  if (!enabled) {
    resetMonetaryRewardFields();
    selectedApproverIds.value = [];
    approverSelectionError.value = '';
  }
});
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/award-form';
</style>
